import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clients } from '@/lib/https';
import { getPreSignedUrlForUpload } from '../services/storage.service';
import {
  GetPreSignedUrlForUploadPayload,
  GetPreSignedUrlForUploadResponse,
} from '../types/storage.types';

export const useGetPreSignedUrlForUpload = () => {
  const queryClient = useQueryClient();

  return useMutation<GetPreSignedUrlForUploadResponse, Error, GetPreSignedUrlForUploadPayload>({
    mutationKey: ['storage', 'file', 'get-presigned-url'],
    mutationFn: getPreSignedUrlForUpload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage', 'file'] });
    },
  });
};

export const useUploadFile = () => {
  return useMutation({
    mutationKey: ['storage', 'file', 'upload'],
    mutationFn: async ({ url, file }: { url?: string; file: File }) => {
      if (!url) throw new Error('Upload URL is missing');

      const response = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'x-ms-blob-type': 'Blockblob',
        },
      });

      if (!response.ok) {
        throw new Error('File upload failed');
      }

      return { uploadUrl: url };
    },
  });
};

export const useDeleteFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['storage', 'file', 'delete'],
    mutationFn: ({ fileId, projectKey }: { fileId: string; projectKey?: string }) =>
      clients.post(
        '/uds/v1/Files/Delete',
        JSON.stringify({
          itemId: fileId,
          ...(projectKey ? { projectKey } : {}),
        })
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage', 'file'] });
    },
  });
};
